/**
 * `requireRole` — loader paramétré qui refuse une zone de routes à qui n'a pas
 * le rôle attendu.
 *
 * @remarks
 * Se déclare par un manifest sur son groupe de routes, pas à la main sur chaque
 * route :
 *
 * ```ts
 * routes: [{ routes: adminUsersRoutes, guard: requireRole(['admin']) }]
 * ```
 *
 * **L'authentification est résolue d'abord, et c'est indispensable.** Au
 * rechargement d'une page, l'access token — qui ne vit qu'en mémoire —
 * a disparu, et `requireAuth` le régénère par silent refresh. Les loaders
 * d'une même hiérarchie s'exécutant en parallèle, lire le store sans attendre
 * renverrait un administrateur vers `/login` au seul motif que le refresh
 * n'était pas terminé. `attemptSilentRefresh` déduplique les appels
 * concurrents : passer par `requireAuth` ici ne coûte pas un second
 * aller-retour réseau.
 *
 * Le rôle manquant redirige vers l'accueil applicatif plutôt que vers une page
 * de refus : sur un back-office dont l'existence n'est pas publique, un « 403 »
 * explicite renseignerait un visiteur sur ce qu'il vient de trouver (SAAS-883).
 */

import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { loginRedirect, requireAuth } from './require-auth';

/** Où atterrit un utilisateur authentifié à qui la zone est refusée. */
const FALLBACK_ROUTE = '/tests';

export function requireRole(allowedRoles: readonly string[]) {
  return async (args: LoaderFunctionArgs): Promise<null | Response> => {
    const authRedirect = await requireAuth(args);
    if (authRedirect) return authRedirect;

    const user = useAuthStore.getState().user;
    if (!user) return loginRedirect(args);

    const hasRole = allowedRoles.some((role) => user.roles.includes(role));
    return hasRole ? null : redirect(FALLBACK_ROUTE);
  };
}
